<script>
import OmniprintApi from '@/api/OmniprintApi';
import LabelPrintingService from '@/services/LabelPrintingService';

export default {
    data() {
        return {
            defaultPrinter: '',
            availablePrinters: [],
            isLoading: false,
        };
    },

    async created() {
        await this.loadPrinters();
    },

    methods: {
        async loadPrinters() {
            this.isLoading = true;

            try {
                this.availablePrinters = await this.getPrintersOrFail();
                await this.loadDefaultPrinter();
            } catch (e) {
                await this.$store.dispatch(
                    'snackbar',
                    {
                        message: 'Failed to get printers',
                        type: 'error',
                        duration: 3000,
                    },
                    { root: true },
                );
            } finally {
                this.isLoading = false;
            }
        },

        async getPrintersOrFail() {
            try {
                const response = await OmniprintApi.getAvailablePrinters();
                if (!response?.data || !response.data.length) {
                    throw new Error('No printers returned from OmniprintApi');
                }
                return response.data;
            } catch {
                const printers = await LabelPrintingService.getConnectedPrinters();

                if (!printers || printers instanceof Error || !printers.length) {
                    throw new Error('Failed to get printers from LabelPrintingService');
                }

                return printers;
            }
        },

        async loadDefaultPrinter() {
            if (!Array.isArray( this.availablePrinters ) || !this.availablePrinters.length) {
                return;
            }

            try {
                const response = await OmniprintApi.getDefaultPrinter();
                const defaultPrinter = response.data.DEFAULT_PRINTER;

                if (defaultPrinter && this.availablePrinters.includes( defaultPrinter )) {
                    this.defaultPrinter = defaultPrinter;
                }
            } catch {
                await this.$store.dispatch(
                    'snackbar',
                    {
                        message: 'No default printer selected, set the default printer',
                        type: 'warning',
                    },
                    { root: true },
                );
            }
        },

        async setDefaultPrinter(defaultPrinterName) {
            try {
                await OmniprintApi.setDefaultPrinter(defaultPrinterName);

                await this.$store.dispatch(
                    'snackbar',
                    {
                        message: 'Successfully updated printer selection!',
                        type: 'success',
                        duration: 3000,
                    },
                    { root: true },
                );
            } catch (error) {
                await this.loadPrinters();

                await this.$store.dispatch(
                    'snackbar',
                    {
                        message: 'Failed to update printer selection.',
                        type: 'error',
                        duration: 3000,
                    },
                    { root: true },
                );
            }
        },
    },
};
</script>

<template>
    <v-container>
        <v-row>
            <v-col>
                <h2>Label Printer Settings</h2>

                <v-select
                    id="printerSelector"
                    v-model="defaultPrinter"
                    label="Default Printer"
                    placeholder="No Default Selected"
                    :items="availablePrinters"
                    :loading="isLoading"
                    :disabled="isLoading"
                />

                <ClButton
                    aria-label="Update Printer Selection"
                    class="button-update"
                    :rounded="false"
                    :disabled="isLoading || !defaultPrinter"
                    @click="setDefaultPrinter(defaultPrinter)"
                >
                    Update Printer Selection
                </ClButton>
            </v-col>
        </v-row>
    </v-container>
</template>
